import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { DictionaryType } from '../../pages/Dictionary/utils/utils';
import { IFieldFieldsFragment } from '../../utils/apollo/graphqlSchema';
import { appRoutes } from '../../path';
import { GoToDictionaryAction } from '../BuilderInfo/components';
import { activeSchemaIdSelector } from '../../redux/selectors/builder';
import {
  getUrlForDataSharing,
  SearchParamsDataSharingEnum,
} from '../../utils/searchParamsDataSharing';
import Linkify from '../Linkify';
import { theme } from '../../styles/theme';

const DataDictContainer = styled.div`
  width: 270px;
  cursor: default;
  padding: 12px;
`;
const StyledHeader = styled.div`
  display: flex;
  column-gap: 4px;
  margin-bottom: 6px;
`;
const DataTitle = styled.div`
  flex: 1;
  max-width: 100%;
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.mainBlue};
  white-space: pre-wrap;
  word-break: break-word;
`;
const DataKey = styled.div`
  width: 100%;
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.secondary900};
  word-break: break-word;
`;
const DataDescription = styled.div`
  margin: 16px 0 0;
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.secondary800};
  white-space: pre-wrap;
  word-break: break-word;
`;

interface IDataDictionaryInfo {
  item: IFieldFieldsFragment;
  type: DictionaryType;
  setDisplayedInfoKey?: (key: string) => void;
}

export default ({ item, type, setDisplayedInfoKey }: IDataDictionaryInfo) => {
  const activeSchemaId = useSelector(activeSchemaIdSelector);

  // close the popup info when mouse click
  const handleClick = useCallback(
    (e: MouseEvent) => {
      const popupElement = document.getElementById(
        'RenderedLabelPopup-' + type,
      );
      const clickedElement: any = e.target;
      // if popup is open and clicked outside of popup container, then close the popup.
      if (
        popupElement &&
        !popupElement?.contains(clickedElement) &&
        setDisplayedInfoKey
      ) {
        setDisplayedInfoKey('');
      }
    },
    [setDisplayedInfoKey, type],
  );
  useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [handleClick]);

  const dictionaryUrl = getUrlForDataSharing(
    appRoutes.dictionary.generateLink(type),
    {
      type: SearchParamsDataSharingEnum.Dictionary,
      schemaId: activeSchemaId,
      term: item.nestedFieldMetadata?.name ?? item.name ?? '',
    },
  );

  return (
    <DataDictContainer>
      <StyledHeader>
        <DataTitle>{item.name}</DataTitle>
        {dictionaryUrl && (
          <GoToDictionaryAction dictionaryUrl={dictionaryUrl} />
        )}
      </StyledHeader>
      <DataKey>{item.key}</DataKey>

      {item.description && item.description.length > 0 && (
        <DataDescription>
          <Linkify text={item.description} />
        </DataDescription>
      )}
    </DataDictContainer>
  );
};
